import { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { API_BASE } from './api';
import { useLocation } from './context/LocationContext';

const CatalogContext = createContext(null);

export const slugify = (s) =>
  String(s || '').toLowerCase().trim().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');

// A product's category slug. The populated `category` is authoritative — the
// denormalised `categorySlug` can lag behind after a category is renamed.
export const catSlugOf = (p) => p?.category?.slug || p?.categorySlug || '';

// Price info for a product: offer price, struck MRP and the discount percent.
export function priceOf(p) {
  if (!p || !p.price) return null;
  const price = p.price;
  const mrp = p.mrp && p.mrp > price ? p.mrp : 0;
  const off = mrp ? Math.round(((mrp - price) / mrp) * 100) : 0;
  return { price, mrp, off };
}

// Pulls everything the admin manages — categories + the full product catalogue —
// so the website lists real products with real prices.
export function CatalogProvider({ children }) {
  const { location } = useLocation();
  const pincode = location?.pincode || '';
  // Pricing resolves PIN → district → state, and the server can't derive a
  // district from a pincode, so send all three.
  const locState = location?.state || '';
  const locDistrict = location?.district || location?.city || '';

  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);

  // Re-fetch whenever the delivery pincode changes so every price on the site
  // reflects that state/district's pricing.
  useEffect(() => {
    let alive = true;
    setLoading(true);

    // Walk the pages so we get the whole catalogue, not just the first 60.
    const fetchAllProducts = async () => {
      const all = [];
      let page = 1;
      let totalPages = 1;
      do {
        const qs = new URLSearchParams({ limit: '60', page: String(page) });
        if (pincode) {
          qs.set('pincode', pincode);
          if (locState) qs.set('state', locState);
          if (locDistrict) qs.set('district', locDistrict);
        }
        const res = await fetch(`${API_BASE}/products?${qs}`);
        const d = await res.json();
        if (!d.success) break;
        all.push(...(d.items || []));
        totalPages = d.totalPages || 1;
        page += 1;
      } while (page <= totalPages && page <= 10);
      return all;
    };

    Promise.all([
      fetchAllProducts().catch(() => []),
      fetch(`${API_BASE}/categories`).then((r) => r.json()).then((d) => d.items || []).catch(() => []),
    ])
      .then(([prods, cats]) => {
        if (!alive) return;
        setProducts(prods);
        setCategories(cats);
      })
      .finally(() => alive && setLoading(false));

    return () => { alive = false; };
  }, [pincode, locState, locDistrict]);

  const value = useMemo(() => {
    // Distinct brands, in the order they appear in the catalogue.
    const brands = [];
    products.forEach((p) => {
      const name = p.brand || '';
      if (name && !brands.some((b) => b.name === name)) {
        brands.push({ name, slug: slugify(name) });
      }
    });

    return {
      loading,
      products,
      categories,
      brands,
      byBrandSlug: (slug) => products.filter((p) => slugify(p.brand) === slug),
      byCategory: (slug) => products.filter((p) => catSlugOf(p) === slug),
      bySlug: (slug) => products.find((p) => p.slug === slug) || null,
      categoryBySlug: (slug) => categories.find((c) => c.slug === slug) || null,
      brandBySlug: (slug) => brands.find((b) => b.slug === slug) || null,
    };
  }, [products, categories, loading]);

  return <CatalogContext.Provider value={value}>{children}</CatalogContext.Provider>;
}

export const useCatalog = () => useContext(CatalogContext);
